// @ts-nocheck
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, X } from 'lucide-react';
import { toast } from 'sonner';
import { usStates } from '@/lib/constants';
import PositionSelect from '@/components/rosters/PositionSelect';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

const GRAD_YEAR_OPTIONS = ['2026', '2027', '2028', '2029', '2030', '2031', '2032'];

function invokeError(error: any, data: any): string | null {
  if (error) return error.message ?? 'Request failed';
  if (data?.ok === false) return data.error?.message ?? 'Request failed';
  return null;
}

interface Props {
  player: any;
  teamId: string;
  eventRegistrationId?: string | null;
  onCancel: () => void;
  onSaved: () => void;
}

export default function PlayerEditForm({ player, teamId, eventRegistrationId, onCancel, onSaved }: Props) {
  const [form, setForm] = useState({
    full_name: player.full_name ?? player.name ?? '',
    grad_year: player.grad_year ? String(player.grad_year) : '',
    position: player.position ?? '',
    home_state: player.home_state ?? '',
    jersey_number: player.jersey_number ? String(player.jersey_number) : '',
    email: player.email ?? '',
    phone: player.phone ?? '',
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k: string) => (e: any) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.full_name.trim()) {
      toast.error('Full name is required.');
      return;
    }
    setSubmitting(true);
    try {
      const payload: any = {
        team_id: teamId,
        player_id: player.player_id,
        full_name: form.full_name.trim(),
        position: form.position || null,
        home_state: form.home_state.trim() || null,
        jersey_number: form.jersey_number.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
      };
      const gy = form.grad_year ? parseInt(form.grad_year, 10) : null;
      if (gy && !Number.isNaN(gy)) payload.grad_year = gy;
      if (eventRegistrationId) payload.event_registration_id = eventRegistrationId;

      const { data, error } = await invokeEdgeFunction('rle-update-roster-player', {
        body: payload,
      });
      const err = invokeError(error, data);
      if (err) throw new Error(err);
      toast.success('Player updated.');
      onSaved();
    } catch (err: any) {
      toast.error(err.message ?? 'Failed to update player');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="space-y-4 rounded-md border bg-muted/30 p-4" onSubmit={handleSave}>
      <Alert className="border-amber-500/50 bg-amber-50/60 dark:bg-amber-950/20 text-amber-900 dark:text-amber-100 [&>svg]:text-amber-600 dark:[&>svg]:text-amber-400">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          <span className="font-semibold">Please do NOT enter the coach&apos;s email for a player.</span>{' '}
          Entering a coach email on a player record will cause the player to be deleted. Leaving the player email blank is completely fine. Adding the athlete&apos;s own email lets the player log in and complete the information required for the digital and printed college-coach packets distributed at the event.
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <Label>Full name *</Label>
          <Input value={form.full_name} onChange={set('full_name')} />
        </div>
        <div>
          <Label>Jersey number</Label>
          <Input value={form.jersey_number} onChange={set('jersey_number')} maxLength={3} />
        </div>
        <div>
          <Label>Position</Label>
          <PositionSelect teamId={teamId} value={form.position} onChange={(v) => setForm((f) => ({ ...f, position: v }))} />
        </div>
        <div>
          <Label>Grad year</Label>
          <Select value={form.grad_year} onValueChange={(v) => setForm((f) => ({ ...f, grad_year: v }))}>
            <SelectTrigger><SelectValue placeholder="Select grad year" /></SelectTrigger>
            <SelectContent>
              {GRAD_YEAR_OPTIONS.map((y) => (<SelectItem key={y} value={y}>{y}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Home state</Label>
          <Select value={form.home_state} onValueChange={(v) => setForm((f) => ({ ...f, home_state: v }))}>
            <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
            <SelectContent>
              {usStates.map((s) => (<SelectItem key={s.abbreviation} value={s.abbreviation}>{s.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>Athlete email</Label>
          <Input type="email" value={form.email} onChange={set('email')} />
        </div>
        <div>
          <Label>Phone</Label>
          <Input formatPhone value={form.phone} onChange={set('phone')} />
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        <Button type="button" size="sm" variant="ghost" onClick={onCancel} disabled={submitting}>
          <X className="mr-1 h-4 w-4" /> Cancel
        </Button>
        <Button type="submit" size="sm" disabled={submitting}>
          {submitting ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </form>
  );
}
